import { createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import { getEnv, getLocalStorageExpire } from 'utils/utils';
import { handleNetworkChange } from 'store/appActions';
import { userLoggedIn, userLoggedOut } from 'store/appThunks';
import { LOGIN_NETWORK_NAME_VERSION, DEMO } from 'utils/constants';
import { DEFAULT_TOKEN, setDemoToken } from 'utils/demoTokens';
import { CONFIG_SLICE_NAME } from '../slicesNames';
import defaultConfig from './localConfigurations/defaultConfig';
import configThunks from './configThunks';
import getClientConfig from './localConfigurations/getClientConfig';
import { availableGroupSlugs as selectAvailableGroupSlugs } from './selectNetworks';
import { isCurrentNetworkValid } from './configUtils';

const localClientConfig = getClientConfig();
export const combinedLocalConfig = { ...defaultConfig, ...localClientConfig };

export const cookieValue = getLocalStorageExpire(
  combinedLocalConfig.LOGIN_NETWORK_NAME,
  LOGIN_NETWORK_NAME_VERSION
);

if (localClientConfig.EMBOLD_CLIENT === DEMO) {
  setDemoToken(DEFAULT_TOKEN);
}

const initialState = {
  isLoading: false,
  isLoaded: false,
  error: null,
  appMounted: false,
  currentNetworkSlug: cookieValue,
  networkGroup: localStorage.getItem('networkGroup'),
  values: {
    ...combinedLocalConfig,
  },
};

const FUSION_DEV_URL = 'https://fusion.dev.emboldhealth.com/api/pg';

// setup local, testing and staging specifics
const env = getEnv();
if (process.env.NODE_ENV === 'development') {
  // determine if we are hitting fusion dev or local fusion
  const apiUrl = localStorage.getItem('devTools:apiUrl') || FUSION_DEV_URL;
  initialState.values.API_URL = apiUrl;
  if (apiUrl !== FUSION_DEV_URL) {
    initialState.values.API_TOKEN = `Token ${process.env.REACT_APP_LOCAL_FUSION_SUPERUSER_TOKEN}`;
  }
} else if (env === 'testing') {
  initialState.values.API_URL = FUSION_DEV_URL;
} else if (env === 'staging') {
  initialState.values.API_URL = 'https://fusion.staging.emboldhealth.com/api/pg';
}

const configSlice = createSlice({
  name: CONFIG_SLICE_NAME,
  initialState,
  reducers: {
    setNetworkSlug(state, action) {
      state.currentNetworkSlug = action.payload;
    },
    overrideValue(state, action) {
      if (process.env.NODE_ENV === 'development') {
        state.values = { ...state.values, ...action.payload };
      }
    },
    appMounted: (state) => {
      state.appMounted = true;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(configThunks.fetchClientConfig.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;

        // set additional state from args
        const { network, networkGroup } = action.meta.arg || {};

        if (network) {
          state.currentNetworkSlug = network;
        }

        if (networkGroup) {
          state.networkGroup = networkGroup;
        }
      })
      .addCase(configThunks.fetchClientConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.error = null;
        state.values = { ...state.values, ...action.payload };

        const availableNetworks = Object.keys(state.values.REGION_SELECTOR_CODES);
        const mpiNetwork = localStorage.getItem('mpiNetwork');

        // if current slug (from previous session) is invalid, clear it
        const validLoginCookie = isCurrentNetworkValid(state.currentNetworkSlug, availableNetworks);

        if (!validLoginCookie) {
          localStorage.removeItem(combinedLocalConfig.LOGIN_NETWORK_NAME);
          state.currentNetworkSlug = null;
        }

        // override the networkSlug if there is portal integration and a valid mpiNetwork
        if (state.values.HAS_PORTAL_INTEGRATION && mpiNetwork) {
          if (isCurrentNetworkValid(mpiNetwork, availableNetworks)) {
            // hide region selector when SSO is used
            state.currentNetworkSlug = mpiNetwork;
            state.values.SHOW_REGION_SELECTOR = false;
          } else {
            localStorage.removeItem('mpiNetwork');
            state.currentNetworkSlug = null;
          }
        }

        // validate that the network group is in the list of available network groups
        if (state.networkGroup) {
          const availableGroupSlugs = selectAvailableGroupSlugs({ [CONFIG_SLICE_NAME]: state });
          if (!availableGroupSlugs.includes(state.networkGroup)) {
            if (state.networkGroup !== 'all') {
              // the network group "all" has conventionally been used for clients to intentionally clear their network group and should not be reported as error
              Sentry.captureMessage(`Invalid network group: ${state.networkGroup}`, (scope) => {
                scope.setLevel('error');
              });
            }
            // when the networkGroup is invalid it should be cleared from state and local storage
            state.networkGroup = null;
            localStorage.removeItem('networkGroup');
          }
        }
      })
      .addCase(configThunks.fetchClientConfig.rejected, (state, action) => {
        state.isLoading = false;
        // error message could be passed with rejectWithValue which puts the message in the action.payload
        // OR errors can be thrown which puts them in action.error
        state.error = action.payload || action.error?.message || 'Unknown Error';
      });

    builder.addCase(handleNetworkChange, (state, action) => {
      const networkSlug = action.payload;
      state.currentNetworkSlug = networkSlug;
    });

    builder
      .addCase(userLoggedIn.fulfilled, (state, action) => {
        const { networkSlug } = action.payload;
        state.currentNetworkSlug = networkSlug;
      })
      .addCase(userLoggedOut.fulfilled, (state) => {
        state.currentNetworkSlug = null;
      });
  },
});

export default configSlice;
export const { setNetworkSlug, appMounted } = configSlice.actions;
